/** venture/Steps11to14.tsx — D배치 신규 11~15단계 (지도사 확정 2026-07-17 · 2026-09-24 13 VICS 삽입으로 15단계)
 *  구 11단계(확인서 출력·수수료 청구)를 4단계로 분해 — 전부 피투자 법인별 진행.
 *  데이터 호환: 신 12(issued)·14(billed/paid)는 구 s11 저장소를 그대로 읽고 씀 → 기존 진행 건 무손실.
 *  신 11(송부)=s11send · 13 VICS 보고·14 투자확인서 발급=s13 저장소 · 15 청구=s11. 파일 ≤200줄. */
import React from 'react';
import { getChk, getUp, getUpKey, mutStore, UpLabel, renameUpload, dispName } from './stepcommon';
import { s3upload } from './api';
import { openS3, downloadUploadFile } from './docgen';
import InvestCertDoc from './InvestCertDoc';
import Step13Vics from './Step13Vics';
import Step14Kit from './Step14Kit';
import Step15Fee from './Step15Fee';
import CoStatBar from './CoStatBar';
import { CredChip } from './VicsDocs';
import { VICS_DOC_SITES } from './vics13';
import type { Project, CoCard, StoreKey } from './types';

interface Props {
  n: 11 | 12 | 13 | 14 | 15;
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
}

function colCls(cnt: number): string { return cnt >= 3 ? 'cols3' : (cnt === 2 ? 'cols2' : 'cols1'); }
const none = (msg: string) => <div className="lbl" style={{ padding: '14px 0', textAlign: 'center' }}>{msg}</div>;

/** 12단계 · 벤처기업확인서 PDF 보관 (s11 `<coId>_vcert`) — 13단계 VICS 1313 첨부·확인번호 확인에 그대로 쓰인다 (2026-09-24) */
function VcertRow({ p, co, onPatch, toast }: { p: Project; co: CoCard; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void }) {
  const key = co.id + '_vcert';
  const name = getUp(p, 's11', key); const s3key = getUpKey(p, 's11', key);
  const disp = name ? dispName(name, '벤처기업확인서', co.name || '법인') : name;   // 항상 「벤처기업확인서_법인명.pdf」
  const upload = async (f0: File) => {
    try {
      const r = await s3upload(p.id, renameUpload(f0, '벤처기업확인서', co.name || '법인'));
      onPatch(mutStore(p, 's11', (st) => { st.up[key] = r.name; st.upKey = st.upKey || {}; st.upKey[key] = r.key; }));
      toast('업로드됨 · ' + r.name + ' — 13단계 VICS 첨부에도 바로 표시');
    } catch (e) { toast((e as Error).message); }
  };
  const remove = () => onPatch(mutStore(p, 's11', (st) => { if (st.up) delete st.up[key]; if (st.upKey) delete st.upKey[key]; }));
  return (
    <div className="codoc" style={{ marginTop: 6 }}>
      <div className="codoc-h"><span>벤처기업확인서 PDF 보관 <span className="s2tag">13단계 VICS 등록 첨부에 사용</span></span></div>
      {name ? (
        <div className="s3file ok"><span className="rf" title={name}>📄 {disp}</span><span className="pill p-green">보관됨</span><span className="grow" />
          <button className="rbtn" onClick={() => openS3(s3key, name, true).catch((e) => toast((e as Error).message))}>👁 보기</button>
          <button className="rbtn" onClick={() => downloadUploadFile(p, s3key, disp, toast)}>⬇</button>
          <UpLabel text="다시" onUp={upload} />
          <button className="rbtn danger" onClick={remove}>🗑</button></div>
      ) : <UpLabel text="⬆ 확인서 PDF 업로드 (또는 드래그)" onUp={upload} />}
    </div>
  );
}

/** 단계별 정의: 저장소 + 체크 항목(키는 구 s11과 호환되도록 유지) */
const DEFS: Record<11 | 12 | 13 | 14 | 15, { title: string; hint: string; store: StoreKey; checks: [string, string][] }> = {
  11: { title: '📮 벤처투자확인서 송부', hint: '법인별 확인서류 송부 여부 (클릭하여 체크)', store: 's11send', checks: [['sent', '송부']] },
  12: { title: '📜 확인서 출력', hint: '법인별 벤처기업확인서 출력·전달', store: 's11', checks: [['issued', '확인서 출력']] }, // 구 issued 그대로 — 기존 체크 유지
  13: { title: '🗂 VICS 투자실적 보고', hint: '법인별 1313·1319·1320 → 조합 [1329]', store: 's13', checks: [] },        // 화면은 Step13Vics
  14: { title: '🧾 투자확인서 발급', hint: '개인투자조합 투자확인서(소득공제용) 발급', store: 's13', checks: [['invcert', '투자확인서 발급']] },   // 화면은 Step14Kit
  15: { title: '💰 수수료 청구', hint: '법인별 수수료 청구·입금 확인', store: 's11', checks: [['billed', '수수료 청구'], ['paid', '입금 확인']] }, // 구 billed/paid 그대로 · 화면은 Step15Fee
};

export default function Steps11to14({ n, p, onPatch, toast }: Props) {
  // 13 VICS 보고 · 14 발급 키트 · 15 청구는 회사별 화면으로 분리 (2026-09-24). 저장 키는 그대로.
  if (n === 13) return <Step13Vics p={p} onPatch={onPatch} toast={toast} />;
  if (n === 14) return <Step14Kit p={p} onPatch={onPatch} toast={toast} />;
  if (n === 15) return <Step15Fee p={p} onPatch={onPatch} toast={toast} />;
  const def = DEFS[n];

  const cos: CoCard[] = (p.collect && p.collect.cos) || [];
  const toggle = (key: string) => onPatch(mutStore(p, def.store, (s) => { s.chk = s.chk || {}; s.chk[key] = !s.chk[key]; }));

  // 법인별 완료 수 → 단계 요약
  const doneCount = cos.filter((co) => def.checks.every((c) => getChk(p, def.store, co.id + '_' + c[0]))).length;

  return (
    <div className="sact">
      <div className="sact-h">{def.title}</div>
      <div className="lbl">{def.hint} · 완료 {doneCount}/{cos.length}개사</div>
      {def.checks.length === 1 ? (
        // 체크 1종: 행 리스트 (10단계 심사비 패턴)
        cos.length ? cos.map((co) => {
          const key = co.id + '_' + def.checks[0][0];
          const done = getChk(p, def.store, key);
          return (
            <div key={co.id} className={n === 11 ? 'corow' : ''} id={'co-' + co.id + '-' + n}>
              <div className={'chkrow ' + (done ? 'on' : '')} onClick={() => toggle(key)}>
                <span className="chkbox">{done ? '✓' : ''}</span><b>{co.name || '(법인)'}</b>
                {n === 12 ? (
                  // 2026-10-08 지도사 「벤처 ID, PW 도 앞 단계에서 입력한 걸 보여줘야지 · 바로가기 버튼도」 — 9단계(또는 1단계 연동) 벤처확인시스템 로그인 · 여기서 고치면 9단계 칸에 저장
                  <span className="s12-login" onClick={(e) => e.stopPropagation()}>
                    <CredChip p={p} co={co} toast={toast} onPatch={onPatch} />
                    <a className="upbtn link sm" href={VICS_DOC_SITES.vcert ? VICS_DOC_SITES.vcert.url : 'https://www.smes.go.kr/venturein/'} target="_blank" rel="noopener noreferrer">🔗 벤처확인시스템 로그인</a>
                  </span>
                ) : null}
                <span className="grow" />
                <span className="chklabel">{def.checks[0][1]} {done ? '완료' : '대기'}</span>
              </div>
              {n === 11 && <InvestCertDoc p={p} co={co} onPatch={onPatch} toast={toast} />}
              {n === 12 && <VcertRow p={p} co={co} onPatch={onPatch} toast={toast} />}
              <CoStatBar p={p} co={co} n={n} onPatch={onPatch} toast={toast} />
            </div>
          );
        }) : none('등록된 투자법인이 없습니다')
      ) : (
        // 체크 복수: 법인 컬럼 그리드 (구 11단계 패턴)
        <div className={'cogrid ' + colCls(cos.length)}>
          {cos.length ? cos.map((co) => (
            <div className="cocol" key={co.id} id={'co-' + co.id + '-' + n}>
              <div className="cocol-h">🏢 {co.name || '(법인)'}</div>
              <CoStatBar p={p} co={co} n={n} onPatch={onPatch} toast={toast} />
              <div className="chkset">
                {def.checks.map((c) => {
                  const done = getChk(p, def.store, co.id + '_' + c[0]);
                  return <button className={'chkpill ' + (done ? 'on' : '')} key={c[0]} onClick={() => toggle(co.id + '_' + c[0])}>{done ? '✓ ' : ''}{c[1]}</button>;
                })}
              </div>
            </div>
          )) : none('등록된 투자법인이 없습니다')}
        </div>
      )}
    </div>
  );
}
